/**
 * 5e class mechanical data — feature names, hit dice, ASI and subclass
 * levels. This is a skeleton: names only, no descriptions. The DM
 * references the full text from the Player's Handbook or D&D Beyond.
 *
 * Sources: 5e SRD 5.1 (Creative Commons) for the mechanical structure;
 * feature names at each level follow the standard published progression.
 *
 * Spell slot tables are NOT included here — they belong on the runtime
 * side (SpellSlots state). The Level Up page will seed appropriate slot
 * counts when a caster levels.
 */

export interface ClassLevel {
  /** Human-readable features gained at this level. */
  features: string[];
}

export interface ClassDef {
  name: string;
  hitDie: number;
  /** Levels that grant an Ability Score Improvement (or feat). */
  asiLevels: number[];
  /** Level at which the subclass is chosen. */
  subclassLevel: number;
  /** Label used in the UI for the subclass choice. */
  subclassLabel: string;
  /** Feature names by level (1-indexed). */
  levels: Record<number, ClassLevel>;
  /** Whether this class is a spellcaster and its casting stat. */
  caster?: { stat: string; type: 'full' | 'half' | 'third' | 'pact' };
}

// ---------- Standard 5e classes ----------

const barbarian: ClassDef = {
  name: 'Barbarian',
  hitDie: 12,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Primal Path',
  levels: {
    1:  { features: ['Rage', 'Unarmored Defense'] },
    2:  { features: ['Reckless Attack', 'Danger Sense'] },
    3:  { features: ['Primal Path'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Extra Attack', 'Fast Movement'] },
    6:  { features: ['Path feature'] },
    7:  { features: ['Feral Instinct'] },
    8:  { features: ['ASI'] },
    9:  { features: ['Brutal Critical (1 die)'] },
    10: { features: ['Path feature'] },
    11: { features: ['Relentless Rage'] },
    12: { features: ['ASI'] },
    13: { features: ['Brutal Critical (2 dice)'] },
    14: { features: ['Path feature'] },
    15: { features: ['Persistent Rage'] },
    16: { features: ['ASI'] },
    17: { features: ['Brutal Critical (3 dice)'] },
    18: { features: ['Indomitable Might'] },
    19: { features: ['ASI'] },
    20: { features: ['Primal Champion'] },
  },
};

const bard: ClassDef = {
  name: 'Bard',
  hitDie: 8,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Bard College',
  caster: { stat: 'CHA', type: 'full' },
  levels: {
    1:  { features: ['Spellcasting', 'Bardic Inspiration (d6)'] },
    2:  { features: ['Jack of All Trades', 'Song of Rest (d6)'] },
    3:  { features: ['Bard College', 'Expertise'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Bardic Inspiration (d8)', 'Font of Inspiration'] },
    6:  { features: ['Countercharm', 'College feature'] },
    7:  { features: [] },
    8:  { features: ['ASI'] },
    9:  { features: ['Song of Rest (d8)'] },
    10: { features: ['Bardic Inspiration (d10)', 'Expertise', 'Magical Secrets'] },
    11: { features: [] },
    12: { features: ['ASI'] },
    13: { features: ['Song of Rest (d10)'] },
    14: { features: ['Magical Secrets', 'College feature'] },
    15: { features: ['Bardic Inspiration (d12)'] },
    16: { features: ['ASI'] },
    17: { features: ['Song of Rest (d12)'] },
    18: { features: ['Magical Secrets'] },
    19: { features: ['ASI'] },
    20: { features: ['Superior Inspiration'] },
  },
};

const cleric: ClassDef = {
  name: 'Cleric',
  hitDie: 8,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 1,
  subclassLabel: 'Divine Domain',
  caster: { stat: 'WIS', type: 'full' },
  levels: {
    1:  { features: ['Spellcasting', 'Divine Domain'] },
    2:  { features: ['Channel Divinity (1/rest)', 'Domain feature'] },
    3:  { features: [] },
    4:  { features: ['ASI'] },
    5:  { features: ['Destroy Undead (CR 1/2)'] },
    6:  { features: ['Channel Divinity (2/rest)', 'Domain feature'] },
    7:  { features: [] },
    8:  { features: ['ASI', 'Destroy Undead (CR 1)', 'Domain feature'] },
    9:  { features: [] },
    10: { features: ['Divine Intervention'] },
    11: { features: ['Destroy Undead (CR 2)'] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Destroy Undead (CR 3)'] },
    15: { features: [] },
    16: { features: ['ASI'] },
    17: { features: ['Destroy Undead (CR 4)', 'Domain feature'] },
    18: { features: ['Channel Divinity (3/rest)'] },
    19: { features: ['ASI'] },
    20: { features: ['Divine Intervention improvement'] },
  },
};

const druid: ClassDef = {
  name: 'Druid',
  hitDie: 8,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 2,
  subclassLabel: 'Druid Circle',
  caster: { stat: 'WIS', type: 'full' },
  levels: {
    1:  { features: ['Druidic', 'Spellcasting'] },
    2:  { features: ['Wild Shape', 'Druid Circle'] },
    3:  { features: [] },
    4:  { features: ['ASI', 'Wild Shape improvement'] },
    5:  { features: [] },
    6:  { features: ['Circle feature'] },
    7:  { features: [] },
    8:  { features: ['ASI', 'Wild Shape improvement'] },
    9:  { features: [] },
    10: { features: ['Circle feature'] },
    11: { features: [] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Circle feature'] },
    15: { features: [] },
    16: { features: ['ASI'] },
    17: { features: [] },
    18: { features: ['Timeless Body', 'Beast Spells'] },
    19: { features: ['ASI'] },
    20: { features: ['Archdruid'] },
  },
};

const fighter: ClassDef = {
  name: 'Fighter',
  hitDie: 10,
  asiLevels: [4, 6, 8, 12, 14, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Martial Archetype',
  levels: {
    1:  { features: ['Fighting Style', 'Second Wind'] },
    2:  { features: ['Action Surge (one use)'] },
    3:  { features: ['Martial Archetype'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Extra Attack'] },
    6:  { features: ['ASI'] },
    7:  { features: ['Archetype feature'] },
    8:  { features: ['ASI'] },
    9:  { features: ['Indomitable (one use)'] },
    10: { features: ['Archetype feature'] },
    11: { features: ['Extra Attack (2)'] },
    12: { features: ['ASI'] },
    13: { features: ['Indomitable (two uses)'] },
    14: { features: ['ASI'] },
    15: { features: ['Archetype feature'] },
    16: { features: ['ASI'] },
    17: { features: ['Action Surge (two uses)', 'Indomitable (three uses)'] },
    18: { features: ['Archetype feature'] },
    19: { features: ['ASI'] },
    20: { features: ['Extra Attack (3)'] },
  },
};

const monk: ClassDef = {
  name: 'Monk',
  hitDie: 8,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Monastic Tradition',
  levels: {
    1:  { features: ['Unarmored Defense', 'Martial Arts'] },
    2:  { features: ['Ki', 'Unarmored Movement'] },
    3:  { features: ['Monastic Tradition', 'Deflect Missiles'] },
    4:  { features: ['ASI', 'Slow Fall'] },
    5:  { features: ['Extra Attack', 'Stunning Strike'] },
    6:  { features: ['Ki-Empowered Strikes', 'Tradition feature'] },
    7:  { features: ['Evasion', 'Stillness of Mind'] },
    8:  { features: ['ASI'] },
    9:  { features: ['Unarmored Movement improvement'] },
    10: { features: ['Purity of Body'] },
    11: { features: ['Tradition feature'] },
    12: { features: ['ASI'] },
    13: { features: ['Tongue of the Sun and Moon'] },
    14: { features: ['Diamond Soul'] },
    15: { features: ['Timeless Body'] },
    16: { features: ['ASI'] },
    17: { features: ['Tradition feature'] },
    18: { features: ['Empty Body'] },
    19: { features: ['ASI'] },
    20: { features: ['Perfect Self'] },
  },
};

const paladin: ClassDef = {
  name: 'Paladin',
  hitDie: 10,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Sacred Oath',
  caster: { stat: 'CHA', type: 'half' },
  levels: {
    1:  { features: ['Divine Sense', 'Lay on Hands'] },
    2:  { features: ['Fighting Style', 'Spellcasting', 'Divine Smite'] },
    3:  { features: ['Sacred Oath', 'Divine Health'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Extra Attack'] },
    6:  { features: ['Aura of Protection'] },
    7:  { features: ['Oath feature'] },
    8:  { features: ['ASI'] },
    9:  { features: [] },
    10: { features: ['Aura of Courage'] },
    11: { features: ['Improved Divine Smite'] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Cleansing Touch'] },
    15: { features: ['Oath feature'] },
    16: { features: ['ASI'] },
    17: { features: [] },
    18: { features: ['Aura improvements'] },
    19: { features: ['ASI'] },
    20: { features: ['Oath feature'] },
  },
};

const ranger: ClassDef = {
  name: 'Ranger',
  hitDie: 10,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Ranger Archetype',
  caster: { stat: 'WIS', type: 'half' },
  levels: {
    1:  { features: ['Favored Enemy', 'Natural Explorer'] },
    2:  { features: ['Fighting Style', 'Spellcasting'] },
    3:  { features: ['Ranger Archetype', 'Primeval Awareness'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Extra Attack'] },
    6:  { features: ['Favored Enemy improvement', 'Natural Explorer improvement'] },
    7:  { features: ['Archetype feature'] },
    8:  { features: ['ASI', 'Land\'s Stride'] },
    9:  { features: [] },
    10: { features: ['Natural Explorer improvement', 'Hide in Plain Sight'] },
    11: { features: ['Archetype feature'] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Favored Enemy improvement', 'Vanish'] },
    15: { features: ['Archetype feature'] },
    16: { features: ['ASI'] },
    17: { features: [] },
    18: { features: ['Feral Senses'] },
    19: { features: ['ASI'] },
    20: { features: ['Foe Slayer'] },
  },
};

const rogue: ClassDef = {
  name: 'Rogue',
  hitDie: 8,
  asiLevels: [4, 8, 10, 12, 16, 19],
  subclassLevel: 3,
  subclassLabel: 'Roguish Archetype',
  levels: {
    1:  { features: ['Expertise', 'Sneak Attack', 'Thieves\' Cant'] },
    2:  { features: ['Cunning Action'] },
    3:  { features: ['Roguish Archetype'] },
    4:  { features: ['ASI'] },
    5:  { features: ['Uncanny Dodge'] },
    6:  { features: ['Expertise'] },
    7:  { features: ['Evasion'] },
    8:  { features: ['ASI'] },
    9:  { features: ['Archetype feature'] },
    10: { features: ['ASI'] },
    11: { features: ['Reliable Talent'] },
    12: { features: ['ASI'] },
    13: { features: ['Archetype feature'] },
    14: { features: ['Blindsense'] },
    15: { features: ['Slippery Mind'] },
    16: { features: ['ASI'] },
    17: { features: ['Archetype feature'] },
    18: { features: ['Elusive'] },
    19: { features: ['ASI'] },
    20: { features: ['Stroke of Luck'] },
  },
};

const sorcerer: ClassDef = {
  name: 'Sorcerer',
  hitDie: 6,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 1,
  subclassLabel: 'Sorcerous Origin',
  caster: { stat: 'CHA', type: 'full' },
  levels: {
    1:  { features: ['Spellcasting', 'Sorcerous Origin'] },
    2:  { features: ['Font of Magic'] },
    3:  { features: ['Metamagic'] },
    4:  { features: ['ASI'] },
    5:  { features: [] },
    6:  { features: ['Origin feature'] },
    7:  { features: [] },
    8:  { features: ['ASI'] },
    9:  { features: [] },
    10: { features: ['Metamagic'] },
    11: { features: [] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Origin feature'] },
    15: { features: [] },
    16: { features: ['ASI'] },
    17: { features: ['Metamagic'] },
    18: { features: ['Origin feature'] },
    19: { features: ['ASI'] },
    20: { features: ['Sorcerous Restoration'] },
  },
};

const warlock: ClassDef = {
  name: 'Warlock',
  hitDie: 8,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 1,
  subclassLabel: 'Otherworldly Patron',
  caster: { stat: 'CHA', type: 'pact' },
  levels: {
    1:  { features: ['Otherworldly Patron', 'Pact Magic'] },
    2:  { features: ['Eldritch Invocations'] },
    3:  { features: ['Pact Boon'] },
    4:  { features: ['ASI'] },
    5:  { features: [] },
    6:  { features: ['Patron feature'] },
    7:  { features: [] },
    8:  { features: ['ASI'] },
    9:  { features: [] },
    10: { features: ['Patron feature'] },
    11: { features: ['Mystic Arcanum (6th level)'] },
    12: { features: ['ASI'] },
    13: { features: ['Mystic Arcanum (7th level)'] },
    14: { features: ['Patron feature'] },
    15: { features: ['Mystic Arcanum (8th level)'] },
    16: { features: ['ASI'] },
    17: { features: ['Mystic Arcanum (9th level)'] },
    18: { features: [] },
    19: { features: ['ASI'] },
    20: { features: ['Eldritch Master'] },
  },
};

const wizard: ClassDef = {
  name: 'Wizard',
  hitDie: 6,
  asiLevels: [4, 8, 12, 16, 19],
  subclassLevel: 2,
  subclassLabel: 'Arcane Tradition',
  caster: { stat: 'INT', type: 'full' },
  levels: {
    1:  { features: ['Spellcasting', 'Arcane Recovery'] },
    2:  { features: ['Arcane Tradition'] },
    3:  { features: [] },
    4:  { features: ['ASI'] },
    5:  { features: [] },
    6:  { features: ['Tradition feature'] },
    7:  { features: [] },
    8:  { features: ['ASI'] },
    9:  { features: [] },
    10: { features: ['Tradition feature'] },
    11: { features: [] },
    12: { features: ['ASI'] },
    13: { features: [] },
    14: { features: ['Tradition feature'] },
    15: { features: [] },
    16: { features: ['ASI'] },
    17: { features: [] },
    18: { features: ['Spell Mastery'] },
    19: { features: ['ASI'] },
    20: { features: ['Signature Spells'] },
  },
};

// ---------- Lookup ----------

export const CLASS_DATA: Record<string, ClassDef> = {
  Barbarian: barbarian,
  Bard: bard,
  Cleric: cleric,
  Druid: druid,
  Fighter: fighter,
  Monk: monk,
  Paladin: paladin,
  Ranger: ranger,
  Rogue: rogue,
  Sorcerer: sorcerer,
  Warlock: warlock,
  Wizard: wizard,
};

/** Case-insensitive class lookup. Returns undefined for homebrew classes. */
export function lookupClass(name: string): ClassDef | undefined {
  // Try exact match first, then title-case.
  const titleCase = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  return CLASS_DATA[name] ?? CLASS_DATA[titleCase];
}

/**
 * Whether a given level is an ASI level for the specified class.
 * Returns false for homebrew / unknown classes (caller should default
 * to the standard 4/8/12/16/19 pattern).
 */
export function isAsiLevel(className: string, level: number): boolean {
  const def = lookupClass(className);
  if (!def) {
    // Fallback: standard ASI levels used by most classes.
    return [4, 8, 12, 16, 19].includes(level);
  }
  return def.asiLevels.includes(level);
}

/**
 * Standard 5e full-caster spell slot table. Index = caster level (1-20).
 * Returns a map of spell level → number of slots.
 */
export const FULL_CASTER_SLOTS: Record<number, Record<number, number>> = {
  1:  { 1: 2 },
  2:  { 1: 3 },
  3:  { 1: 4, 2: 2 },
  4:  { 1: 4, 2: 3 },
  5:  { 1: 4, 2: 3, 3: 2 },
  6:  { 1: 4, 2: 3, 3: 3 },
  7:  { 1: 4, 2: 3, 3: 3, 4: 1 },
  8:  { 1: 4, 2: 3, 3: 3, 4: 2 },
  9:  { 1: 4, 2: 3, 3: 3, 4: 3, 5: 1 },
  10: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2 },
  11: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },
  12: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1 },
  13: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },
  14: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1 },
  15: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },
  16: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1 },
  17: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2, 6: 1, 7: 1, 8: 1, 9: 1 },
  18: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 1, 7: 1, 8: 1, 9: 1 },
  19: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 1, 8: 1, 9: 1 },
  20: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 3, 6: 2, 7: 2, 8: 1, 9: 1 },
};

/**
 * Standard 5e half-caster spell slot table. Index = class level (2-20).
 * Paladins and Rangers don't get spells until level 2.
 */
export const HALF_CASTER_SLOTS: Record<number, Record<number, number>> = {
  2:  { 1: 2 },
  3:  { 1: 3 },
  4:  { 1: 3 },
  5:  { 1: 4, 2: 2 },
  6:  { 1: 4, 2: 2 },
  7:  { 1: 4, 2: 3 },
  8:  { 1: 4, 2: 3 },
  9:  { 1: 4, 2: 3, 3: 2 },
  10: { 1: 4, 2: 3, 3: 2 },
  11: { 1: 4, 2: 3, 3: 3 },
  12: { 1: 4, 2: 3, 3: 3 },
  13: { 1: 4, 2: 3, 3: 3, 4: 1 },
  14: { 1: 4, 2: 3, 3: 3, 4: 1 },
  15: { 1: 4, 2: 3, 3: 3, 4: 2 },
  16: { 1: 4, 2: 3, 3: 3, 4: 2 },
  17: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 1 },
  18: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 1 },
  19: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2 },
  20: { 1: 4, 2: 3, 3: 3, 4: 3, 5: 2 },
};

/**
 * Warlock pact magic slots. Index = class level.
 * Returns { slotLevel, slotCount }.
 */
export const PACT_MAGIC: Record<number, { slotLevel: number; slots: number }> = {
  1:  { slotLevel: 1, slots: 1 },
  2:  { slotLevel: 1, slots: 2 },
  3:  { slotLevel: 2, slots: 2 },
  4:  { slotLevel: 2, slots: 2 },
  5:  { slotLevel: 3, slots: 2 },
  6:  { slotLevel: 3, slots: 2 },
  7:  { slotLevel: 4, slots: 2 },
  8:  { slotLevel: 4, slots: 2 },
  9:  { slotLevel: 5, slots: 2 },
  10: { slotLevel: 5, slots: 2 },
  11: { slotLevel: 5, slots: 3 },
  12: { slotLevel: 5, slots: 3 },
  13: { slotLevel: 5, slots: 3 },
  14: { slotLevel: 5, slots: 3 },
  15: { slotLevel: 5, slots: 3 },
  16: { slotLevel: 5, slots: 3 },
  17: { slotLevel: 5, slots: 4 },
  18: { slotLevel: 5, slots: 4 },
  19: { slotLevel: 5, slots: 4 },
  20: { slotLevel: 5, slots: 4 },
};

// ---------- Spell slot helpers ----------

export interface SpellSlotInfo {
  /** Leveled spell slots: spell level → count. */
  leveled: Record<number, number>;
  /** Pact magic slots (Warlock only). Null if not a pact caster. */
  pact: { slotLevel: number; slots: number } | null;
}

/**
 * Get the spell slot profile for a class at a given level.
 * Returns null for non-casters. For pact casters (Warlock), leveled
 * is empty and pact is populated. For full/half casters, pact is null.
 */
export function getSpellSlots(className: string, level: number): SpellSlotInfo | null {
  const def = lookupClass(className);
  if (!def?.caster) return null;

  if (def.caster.type === 'pact') {
    const pact = PACT_MAGIC[level] ?? null;
    return { leveled: {}, pact };
  }

  const table = def.caster.type === 'full' ? FULL_CASTER_SLOTS : HALF_CASTER_SLOTS;
  const leveled = table[level] ?? {};
  return { leveled, pact: null };
}

/**
 * Compute the diff between old and new spell slot counts.
 * Returns only changed or new slot levels.
 */
export function spellSlotDiff(
  className: string,
  oldLevel: number,
  newLevel: number,
): { levelChanges: Record<number, { from: number; to: number }>; pactChange: string | null } {
  const oldSlots = getSpellSlots(className, oldLevel);
  const newSlots = getSpellSlots(className, newLevel);

  const levelChanges: Record<number, { from: number; to: number }> = {};
  let pactChange: string | null = null;

  if (!newSlots) return { levelChanges, pactChange };

  // Leveled slots diff
  const allLevels = new Set([
    ...Object.keys(oldSlots?.leveled ?? {}),
    ...Object.keys(newSlots.leveled),
  ].map(Number));

  for (const lv of allLevels) {
    const from = oldSlots?.leveled[lv] ?? 0;
    const to = newSlots.leveled[lv] ?? 0;
    if (from !== to) {
      levelChanges[lv] = { from, to };
    }
  }

  // Pact magic diff
  if (newSlots.pact) {
    const oldPact = oldSlots?.pact;
    if (!oldPact || oldPact.slotLevel !== newSlots.pact.slotLevel || oldPact.slots !== newSlots.pact.slots) {
      const fromStr = oldPact ? `${oldPact.slots}x Lv${oldPact.slotLevel}` : 'none';
      const toStr = `${newSlots.pact.slots}x Lv${newSlots.pact.slotLevel}`;
      pactChange = `${fromStr} → ${toStr}`;
    }
  }

  return { levelChanges, pactChange };
}
